import { ref, set, get, runTransaction } from "firebase/database";
import { database, ensureSignedIn } from "../config/firebase";

// Saved teams (reusable player lists) are stored under `teams/{key}` in Firebase
// and mirrored to localStorage so a user's own teams work offline and can be
// offered as quick-pick suggestions. No login: a team is identified purely by a
// memorable name/code, and anyone who knows the name can load or overwrite it.

const LOCAL_TEAMS_KEY = 'vamos-tamos-teams';
const REMOTE_WAIT_MS = 1500;
const teamSaveQueues = new Map();
const PIN_HASH_ITERATIONS = 210000;
const PIN_HASH_PREFIX = 'v2:';

const bytesToHex = (bytes) => Array.from(bytes)
  .map((b) => b.toString(16).padStart(2, '0'))
  .join('');

const bytesToBase64 = (bytes) => btoa(
  Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
);

const base64ToBytes = (value) => {
  try {
    const binary = atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch (error) {
    return null;
  }
};

// Normalize a team name into a safe, collision-resistant Firebase key.
// RTDB keys may not contain `.`, `#`, `$`, `[`, `]`, `/` or control chars.
export const normalizeTeamKey = (name) =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.#$/[\]]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, '');

export const isValidTeamName = (name) => normalizeTeamKey(name).length > 0;

// A PIN is exactly 4 digits.
export const isValidPin = (pin) => /^\d{4}$/.test(pin || '');

// Legacy hash retained so teams saved by earlier releases remain loadable.
export const hashPin = async (pin, name) => {
  const text = `${normalizeTeamKey(name)}:${pin}`;
  const data = new TextEncoder().encode(text);
  const buffer = await crypto.subtle.digest('SHA-256', data);
  return bytesToHex(new Uint8Array(buffer));
};

const createPinCredentials = async (pin, name) => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(`${normalizeTeamKey(name)}:${pin}`),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const derived = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PIN_HASH_ITERATIONS },
    keyMaterial,
    128
  );
  const packed = new Uint8Array(32);
  packed.set(salt, 0);
  packed.set(new Uint8Array(derived), 16);
  return { pinHash: `${PIN_HASH_PREFIX}${bytesToBase64(packed)}` };
};

const verifyModernPin = async (pin, name, record) => {
  const packed = base64ToBytes(record.pinHash.slice(PIN_HASH_PREFIX.length));
  if (!packed || packed.length !== 32) return false;
  const salt = packed.slice(0, 16);
  const expectedHash = packed.slice(16);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(`${normalizeTeamKey(name)}:${pin}`),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const derived = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: PIN_HASH_ITERATIONS },
    keyMaterial,
    128
  );
  const actualHash = new Uint8Array(derived);
  let difference = 0;
  for (let index = 0; index < actualHash.length; index++) {
    difference |= actualHash[index] ^ expectedHash[index];
  }
  return difference === 0;
};

// Does this team record require a PIN to load?
export const teamHasPin = (record) => Boolean(record && record.pinHash);

// Verify a PIN against a loaded team record.
export const verifyPin = async (pin, name, record) => {
  if (!record || !record.pinHash) return true; // no PIN set => open
  if (!isValidPin(pin)) return false;
  if (typeof record.pinHash === 'string' && record.pinHash.startsWith(PIN_HASH_PREFIX)) {
    return verifyModernPin(pin, name, record);
  }
  // Backward compatibility for teams created with the original single-pass hash.
  const hash = await hashPin(pin, name);
  return hash === record.pinHash;
};

// Suggest an available team name based on the requested one. Tries the provided
// (language-specific) suffixes first, verifying each is free, then falls back to
// numbers. Pass suffixes from translations/teamSuffixes.js (getTeamSuffixes).
export const suggestTeamName = async (baseName, suffixes = []) => {
  const base = (baseName || '').trim();
  if (!base) return '';

  const tried = new Set([normalizeTeamKey(base)]);
  const pool = Array.isArray(suffixes) ? [...suffixes] : [];
  const shuffled = pool.sort(() => Math.random() - 0.5);

  for (const suffix of shuffled) {
    const candidate = `${base} ${suffix}`;
    const key = normalizeTeamKey(candidate);
    if (tried.has(key)) continue;
    tried.add(key);
    // eslint-disable-next-line no-await-in-loop
    if (!(await teamExists(candidate))) return candidate;
  }

  for (let i = 2; i < 100; i++) {
    const candidate = `${base} ${i}`;
    // eslint-disable-next-line no-await-in-loop
    if (!(await teamExists(candidate))) return candidate;
  }

  return `${base} ${Date.now().toString(36)}`;
};

// players: [{ name, gender }] -> trimmed, gender defaults to 'male'
const sanitizePlayers = (players) =>
  (Array.isArray(players) ? players : [])
    .map((p) => ({
      name: (p?.name || '').trim(),
      gender: p?.gender === 'female' ? 'female' : 'male',
    }))
    .filter((p) => p.name);

// --- localStorage cache (the user's own teams; offline quick-pick) ---
const readLocalTeams = () => {
  try {
    const raw = localStorage.getItem(LOCAL_TEAMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.warn('Unable to read cached teams', error);
    return {};
  }
};

const writeLocalTeams = (teams) => {
  try {
    localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(teams));
    return true;
  } catch (error) {
    console.warn('Unable to cache teams locally', error);
    return false;
  }
};

const cacheTeam = (key, record) => {
  const teams = readLocalTeams();
  teams[key] = record;
  return writeLocalTeams(teams);
};

// Remote writes remain queued in the SDK when disconnected. Bound only the UI wait;
// keep observing their result, and retain unsynced records across reloads.
const waitForRemote = (operation, fallback) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.resolve(fallback);
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), REMOTE_WAIT_MS);
    operation.then((result) => {
      clearTimeout(timer);
      resolve(result);
    }, () => {
      clearTimeout(timer);
      resolve(fallback);
    });
  });
};

const syncTeam = (key, record) => {
  const previous = teamSaveQueues.get(key);
  // A load while a save is queued should reuse the write, not enqueue it again.
  if (previous?.version === record.localVersion) return previous.promise;
  const operation = (previous?.promise || Promise.resolve()).then(async () => {
    try {
      const user = await ensureSignedIn();
      if (!user) return { synced: false };
      const payload = {
        displayName: record.displayName,
        players: record.players.map((p) => ({ n: p.name, g: p.gender })),
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        ...(record.pinHash ? { pinHash: record.pinHash } : {}),
        ...(typeof record.mixed === 'boolean' ? { mixed: record.mixed } : {}),
      };
      let conflict = false;
      if (record.pendingCreate) {
        // Retrying our own acknowledged creation is safe after an offline reload.
        const result = await runTransaction(ref(database, `teams/${key}`), (current) => {
          const isOwnCreation = current && record.pinHash
            && current.pinHash === record.pinHash && current.createdAt === record.createdAt;
          return current === null || isOwnCreation ? payload : undefined;
        }, { applyLocally: false });
        conflict = !result.committed;
      } else {
        await set(ref(database, `teams/${key}`), payload);
      }
      const cached = readLocalTeams()[key];
      if (cached?.localVersion === record.localVersion) {
        cacheTeam(key, {
          ...cached,
          syncPending: conflict,
          pendingCreate: conflict,
          syncConflict: conflict,
        });
      }
      return conflict ? { synced: false, error: 'name-taken' } : { synced: true };
    } catch (error) {
      console.error('Error saving team to Firebase:', error);
      return { synced: false };
    }
  });
  teamSaveQueues.set(key, { version: record.localVersion, promise: operation });
  operation.then(() => {
    if (teamSaveQueues.get(key)?.promise === operation) teamSaveQueues.delete(key);
  });
  return operation;
};

// Locally cached teams, most recently updated first.
export const getCachedTeams = () =>
  Object.values(readLocalTeams())
    .filter((team) => team && team.displayName)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

export const removeCachedTeam = (name) => {
  const key = normalizeTeamKey(name);
  if (!key) return;
  const teams = readLocalTeams();
  if (teams[key]) {
    delete teams[key];
    writeLocalTeams(teams);
  }
};

// --- Does a team with this name already exist (locally or in Firebase)? ---
export const teamExists = async (name) => {
  if (!isValidTeamName(name)) return false;
  const key = normalizeTeamKey(name);
  if (readLocalTeams()[key]) return true;
  if (!database) return false;
  const check = (async () => {
    try {
      const user = await ensureSignedIn();
      if (!user) return false;
      const snapshot = await get(ref(database, `teams/${key}`));
      return snapshot.exists();
    } catch (error) {
      console.error('Error checking team existence:', error);
      return false;
    }
  })();
  return waitForRemote(check, false);
};

// --- Save / overwrite a team. Always caches locally; syncs to Firebase if available. ---
// pin: 4-digit string to set/replace the PIN. If omitted, an existing PIN is preserved.
// mixed: boolean for the mixed-pairs setting. If omitted, an existing value is preserved.
export const saveTeam = async (name, players, pin = null, mixed = null, { createOnly = false } = {}) => {
  if (!isValidTeamName(name)) {
    return { success: false, error: 'invalid-name' };
  }
  const cleanPlayers = sanitizePlayers(players);
  if (cleanPlayers.length === 0) {
    return { success: false, error: 'no-players' };
  }

  const key = normalizeTeamKey(name);
  const now = Date.now();
  const existing = readLocalTeams()[key];
  if (createOnly && existing) return { success: false, error: 'name-taken' };

  // New PIN provided => hash it; otherwise keep whatever the team already had.
  let pinCredentials = existing?.pinHash ? {
    pinHash: existing.pinHash,
  } : {};
  if (pin != null && pin !== '') {
    if (!isValidPin(pin)) {
      return { success: false, error: 'invalid-pin' };
    }
    pinCredentials = await createPinCredentials(pin, name);
  }

  // Preserve the existing mixed setting unless a new one is explicitly passed.
  const mixedFlag = typeof mixed === 'boolean' ? mixed : existing?.mixed;

  const record = {
    displayName: name.trim(),
    players: cleanPlayers,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    localVersion: `${now}-${Math.random().toString(36).slice(2)}`,
    syncPending: true,
    pendingCreate: createOnly || !existing || Boolean(existing.pendingCreate),
    ...pinCredentials,
    ...(typeof mixedFlag === 'boolean' ? { mixed: mixedFlag } : {}),
  };

  const cached = cacheTeam(key, record);
  const fallback = cached
    ? { success: true, synced: false }
    : { success: false, error: 'save-failed' };
  if (!database) return fallback;

  const remote = syncTeam(key, record).then((result) => {
    if (result.error === 'name-taken') {
      // Keep the local roster for recovery; it must never authorize overwriting
      // the other person's remote team, even on a subsequent update/reload.
      return { success: false, error: 'name-taken' };
    }
    return result.synced ? { success: true, synced: true } : fallback;
  });
  return waitForRemote(remote, fallback);
};

// --- Load a team (Firebase first, localStorage fallback). Returns { displayName, players } or null. ---
export const loadTeam = async (name) => {
  if (!isValidTeamName(name)) return null;
  const key = normalizeTeamKey(name);

  const cached = readLocalTeams()[key];
  if (cached?.syncPending) {
    if (database && !cached.syncConflict) syncTeam(key, cached);
    return cached;
  }
  if (!database) return cached || null;

  const remote = (async () => {
    try {
      const user = await ensureSignedIn();
      if (!user) return cached || null;
      const snapshot = await get(ref(database, `teams/${key}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const players = Array.isArray(data.players)
          ? sanitizePlayers(data.players.map((p) => ({ name: p?.n, gender: p?.g })))
          : [];
        const record = {
          displayName: data.displayName || name.trim(),
          players,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          ...(data.pinHash ? { pinHash: data.pinHash } : {}),
          ...(typeof data.mixed === 'boolean' ? { mixed: data.mixed } : {}),
        };
        // A save may have started while this read was in flight.
        const current = readLocalTeams()[key];
        if (current?.syncPending || current?.localVersion !== cached?.localVersion) return current || record;
        cacheTeam(key, record);
        return record;
      }
    } catch (error) {
      console.error('Error loading team from Firebase:', error);
    }
    return readLocalTeams()[key] || null;
  })();
  return waitForRemote(remote, cached || null);
};
